import type { Match } from '@airmail/engine';
import { useTurnier } from '@/store/TournamentProvider';
import { cx } from '@/ui';
import { useT } from '@/i18n';

/**
 * Live-Spielstand (docs/notes/livescore.md): Cornhole schreibt je Inning nur
 * der besseren Seite die Differenz gut — 1 Punkt je Brett, 3 je Loch. Darum
 * genügen vier große Knöpfe je Seite: +1, +2, +3 und ein „−1" für Vertipper.
 * Der Stand landet als `match.score` im Protokoll (still, ohne Toast) und
 * geht damit über den Sync an Beamer und Zuschauer.
 */

const MIN = 0;
const MAX = 99;
const clamp = (n: number): number => Math.max(MIN, Math.min(MAX, n));

export function LiveScore({ match, teamA, teamB, compact }: { match: Match; teamA: string; teamB: string; compact?: boolean }) {
  const t = useT();
  const { dispatch } = useTurnier();
  if (match.status !== 'running') return null;
  const a = match.live?.a ?? 0;
  const b = match.live?.b ?? 0;
  const bump = (side: 'a' | 'b', by: number) => {
    const next = side === 'a' ? { a: clamp(a + by), b } : { a, b: clamp(b + by) };
    dispatch({ type: 'match.score', matchId: match.id, a: next.a, b: next.b }, { silent: true });
  };

  return (
    <div className="border-t border-line2 mt-3 pt-3">
      <div className="label mb-2">{t('Zwischenstand')}</div>
      <div className="grid grid-cols-2 gap-3">
        <Side team={teamA} value={a} tone="red" compact={compact} onBump={(by) => bump('a', by)} />
        <Side team={teamB} value={b} tone="blue" compact={compact} onBump={(by) => bump('b', by)} right />
      </div>
    </div>
  );
}

function Side({ team, value, tone, compact, right, onBump }: { team: string; value: number; tone: 'red' | 'blue'; compact?: boolean; right?: boolean; onBump: (by: number) => void }) {
  const t = useT();
  return (
    <div className="min-w-0">
      <div className={cx('text-xs text-dim truncate', right && 'text-right')}>{team}</div>
      <div className={cx('font-mono leading-none tracking-tight tabular-nums', compact ? 'text-[34px]' : 'text-[46px]', right && 'text-right', tone === 'red' ? 'text-red' : 'text-blue')}>{value}</div>
      <div className="grid grid-cols-4 gap-1 mt-2">
        {[1, 2, 3].map((n) => (
          <Key key={n} onClick={() => onBump(n)} title={n === 1 ? t('1 Punkt (Brett)') : n === 3 ? t('3 Punkte (Loch)') : t('{n} Punkte', { n })} label={t('{team} +{n}', { team, n })}>
            +{n}
          </Key>
        ))}
        <Key onClick={() => onBump(-1)} title={t('Punkt zurücknehmen')} label={t('{team} −1', { team })} muted>
          −1
        </Key>
      </div>
    </div>
  );
}

function Key({ children, onClick, title, label, muted }: { children: React.ReactNode; onClick: () => void; title: string; label: string; muted?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      className={cx(
        'min-h-11 border border-line font-mono text-[15px] cursor-pointer flex items-center justify-center select-none touch-manipulation hover:bg-panel2',
        muted ? 'bg-transparent text-dim' : 'bg-raise text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Reine Anzeige (Beamer, Zuschauer): „14 : 9", solange ein Zwischenstand da ist */
export function LiveScoreBadge({ match, className }: { match: Match; className?: string }) {
  if (!match.live) return null;
  return (
    <span className={cx('font-mono tabular-nums whitespace-nowrap', className)}>
      {match.live.a} : {match.live.b}
    </span>
  );
}
